import React from 'react';
import * as Icons from 'lucide-react';

export const HeroSectionPreview = ({ component }) => {
  const { specificAttrs = {} } = component;
  const {
    layout = 'center-aligned',
    heading = 'Welcome to Our Platform',
    subheading = 'Build amazing experiences with our powerful tools',
    description = '',
    showImage = true,
    imageUrl = '',
    imagePosition = 'right',
    backgroundType = 'solid',
    backgroundColor = '#1F2937',
    backgroundGradientStart = '#1F2937',
    backgroundGradientEnd = '#3B82F6',
    backgroundImageUrl = '',
    textColor = '#FFFFFF',
    buttons = [],
    height = 'medium',
    verticalAlignment = 'center'
  } = specificAttrs;

  // Height mapping
  const heightMap = {
    small: '300px',
    medium: '500px',
    large: '700px',
    'full-screen': '100vh'
  };

  // Vertical alignment mapping
  const alignmentMap = {
    top: 'flex-start',
    center: 'center',
    bottom: 'flex-end'
  };

  // Background style
  const getBackgroundStyle = () => {
    if (backgroundType === 'gradient') {
      return {
        background: `linear-gradient(135deg, ${backgroundGradientStart} 0%, ${backgroundGradientEnd} 100%)`
      };
    } else if (backgroundType === 'image' && backgroundImageUrl) {
      return {
        backgroundImage: `url(${backgroundImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    } else {
      return {
        backgroundColor: backgroundColor
      };
    }
  };

  // Button variant styles
  const getButtonStyle = (button) => {
    const baseStyle = {
      padding: button.size === 'small' ? '8px 16px' : button.size === 'large' ? '14px 32px' : '10px 24px',
      fontSize: button.size === 'small' ? '13px' : button.size === 'large' ? '16px' : '14px',
      borderRadius: '6px',
      border: 'none',
      cursor: 'pointer',
      fontWeight: '600',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s'
    };

    if (button.variant === 'primary') {
      return {
        ...baseStyle,
        backgroundColor: '#3B82F6',
        color: '#FFFFFF'
      };
    } else if (button.variant === 'secondary') {
      return {
        ...baseStyle,
        backgroundColor: 'transparent',
        color: textColor,
        border: `2px solid ${textColor}`
      };
    } else if (button.variant === 'ghost') {
      return {
        ...baseStyle,
        backgroundColor: 'transparent',
        color: textColor
      };
    }
    return baseStyle;
  };

  // Layout-specific content rendering
  const renderContent = () => {
    const contentStyle = {
      color: textColor,
      maxWidth: layout === 'full-width' ? '100%' : '800px',
      textAlign: layout === 'center-aligned' || layout === 'minimal' ? 'center' : 'left'
    };

    return (
      <div style={contentStyle}>
        <h1 style={{
          fontSize: layout === 'minimal' ? '2.5rem' : '3rem',
          fontWeight: '700',
          marginBottom: '16px',
          lineHeight: '1.2'
        }}>
          {heading}
        </h1>
        
        {subheading && (
          <p style={{
            fontSize: '1.25rem',
            marginBottom: '16px',
            opacity: 0.9
          }}>
            {subheading}
          </p>
        )}
        
        {description && (
          <p style={{
            fontSize: '1rem',
            marginBottom: '24px',
            opacity: 0.8,
            maxWidth: '600px',
            margin: layout === 'center-aligned' ? '0 auto 24px' : '0 0 24px'
          }}>
            {description}
          </p>
        )}
        
        {buttons.length > 0 && (
          <div style={{
            display: 'flex',
            gap: '12px',
            marginTop: '24px',
            justifyContent: layout === 'center-aligned' || layout === 'minimal' ? 'center' : 'flex-start',
            flexWrap: 'wrap'
          }}>
            {buttons.map((button, index) => {
              const IconComponent = button.icon && Icons[button.icon] ? Icons[button.icon] : null;
              return (
                <button
                  key={button.id || index}
                  style={getButtonStyle(button)}
                >
                  {IconComponent && <IconComponent size={18} />}
                  {button.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Split screen layout
  if (layout === 'split-screen' && showImage && imageUrl) {
    return (
      <div style={{
        ...getBackgroundStyle(),
        minHeight: heightMap[height],
        display: 'flex',
        alignItems: alignmentMap[verticalAlignment],
        padding: '40px'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: imagePosition === 'left' ? '1fr 1fr' : '1fr 1fr',
          gap: '40px',
          width: '100%',
          maxWidth: '1200px',
          margin: '0 auto',
          alignItems: 'center'
        }}>
          {imagePosition === 'left' && (
            <div>
              <img
                src={imageUrl}
                alt="Hero"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  objectFit: 'cover'
                }}
              />
            </div>
          )}
          
          <div style={{ color: textColor }}>
            {renderContent()}
          </div>
          
          {imagePosition === 'right' && (
            <div>
              <img
                src={imageUrl}
                alt="Hero"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  objectFit: 'cover'
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default centered layout
  return (
    <div style={{
      ...getBackgroundStyle(),
      minHeight: heightMap[height],
      display: 'flex',
      alignItems: alignmentMap[verticalAlignment],
      justifyContent: 'center',
      padding: '40px 20px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background image for image position background */}
      {showImage && imageUrl && imagePosition === 'background' && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.3,
          zIndex: 0
        }} />
      )}
      
      <div style={{
        position: 'relative',
        zIndex: 1,
        width: '100%',
        maxWidth: '1200px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: layout === 'center-aligned' || layout === 'minimal' ? 'center' : 'flex-start'
      }}>
        {renderContent()}
        
        {/* Image below content if position is top */}
        {showImage && imageUrl && imagePosition === 'top' && (
          <div style={{ marginTop: '32px', width: '100%', maxWidth: '600px' }}>
            <img
              src={imageUrl}
              alt="Hero"
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '8px',
                objectFit: 'cover'
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};